import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { cn } from '../../../lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipPortal = TooltipPrimitive.Portal;

const tooltipContentVariants = cva(
  [
    'z-50 outline-none',
    'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0',
  ],
  {
    variants: {
      variant: {
        /** Caixa de popover (borda + sombra), sem seta — o estilo histórico das telas. */
        default:
          'max-w-xs rounded-md border border-border bg-popover px-3 py-2 text-xs leading-relaxed text-popover-foreground shadow-md',
        /**
         * Componente "Tooltip" do Figma (Job description 20080:1386): fundo muted,
         * texto foreground 12/16, uma linha, com a seta (Caret) apontando o gatilho.
         */
        muted:
          'whitespace-nowrap rounded-sm bg-muted px-3 py-1.5 text-xs leading-4 tracking-[0.01em] text-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

/** Lado do quadrado da seta (Caret do Figma), antes da rotação de 45°. */
const ARROW_SIZE_PX = 10;

export interface TooltipContentProps
  extends
    ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
    VariantProps<typeof tooltipContentVariants> {}

const TooltipContent = forwardRef<ElementRef<typeof TooltipPrimitive.Content>, TooltipContentProps>(
  ({ className, sideOffset = 4, variant, children, ...props }, ref) => {
    const withArrow = variant === 'muted';
    return (
      <TooltipPortal>
        <TooltipPrimitive.Content
          ref={ref}
          // O Radix soma a altura da seta ao offset; desconta para a CAIXA ficar a
          // `sideOffset` do gatilho, como no Figma (a seta avança sobre o gatilho).
          sideOffset={withArrow ? sideOffset - ARROW_SIZE_PX : sideOffset}
          data-slot="tooltip-content"
          className={cn(tooltipContentVariants({ variant }), className)}
          {...props}
        >
          {children}
          {withArrow ? (
            // Caret do Figma: quadrado de 10px girado 45°, cantos de 2px, centrado na
            // borda da caixa (o Radix posiciona e gira o wrapper conforme o lado).
            <TooltipPrimitive.Arrow asChild width={ARROW_SIZE_PX} height={ARROW_SIZE_PX}>
              <span
                data-slot="tooltip-arrow"
                className="block size-2.5 -translate-y-1/2 rotate-45 rounded-xs bg-muted"
              />
            </TooltipPrimitive.Arrow>
          ) : null}
        </TooltipPrimitive.Content>
      </TooltipPortal>
    );
  }
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
  tooltipContentVariants,
};
